/**
 * SegmentAccordion Component - Afișare segmente anatomice sub fiecare studiu
 * 
 * Features:
 * - Accordion MUI expandabil/collapsable
 * - Lista segmente cu: body_part_code, display_name, contrast_status, assigned_to, status
 * - Butoane per segment:
 *   - View (OHIF viewer filtrat pe series UIDs ale segmentului)
 *   - Raport (ReportEditor cu segmentId preset)
 *   - Asociere medic (dialog - doar admin/coordinator)
 *   - Dezasociere medic (doar admin/coordinator)
 * - Color coding: pending (yellow), confirmed (green), rejected (red)
 * - Permissions: Admin/Coordinator pot asocia, Medic poate doar vizualiza
 * 
 * Props:
 * - studyId: UUID studiu părinte
 * - onSegmentUpdate: Callback după update (pentru refresh)
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Collapse
} from '@mui/material';
import {
  Visibility as ViewIcon,
  PersonAdd as AssignIcon,
  PersonRemove as UnassignIcon,
  CheckCircle as ConfirmedIcon,
  PendingActions as PendingIcon,
  Cancel as RejectedIcon,
  Check as ConfirmIcon,
  Close as RejectIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { openOHIFViewer } from '../../utils/ohifViewer';
import SegmentAssignDialog from './SegmentAssignDialog';
import { SegmentEditDialog } from './SegmentEditDialog';
import RadiationDosePanel from './RadiationDosePanel';

interface Segment {
  id: string;
  study_id: string;
  body_part_code: string;
  display_name?: string;  // Display name from segment_definition
  modality: string;
  contrast_status: string;
  series_uids: string[];
  series_count: number;
  confidence: number;
  detection_method: string;
  status: string;
  assigned_to_doctor_id: string | null;
  assigned_doctor_name: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
}

interface SegmentAccordionProps {
  studyId: string;
  studyUid?: string;
  pacsUrl?: string;
  onSegmentUpdate?: () => void;
  expanded: boolean;
  onToggle: () => void;
  hideActions?: boolean;
  refreshKey?: string | number; // Triggers reload when changed
}

const SegmentAccordion: React.FC<SegmentAccordionProps> = ({
  studyId,
  studyUid,
  pacsUrl,
  onSegmentUpdate,
  expanded,
  onToggle: _onToggle,
  hideActions = false,
  refreshKey
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState<Segment | null>(null);
  const [editingSegmentId, setEditingSegmentId] = useState<string | null>(null);
  
  // Check if user can manage segments (assign/unassign/edit) - admin/coordinator only
  const canManageSegments = user?.role === 'admin' || user?.role === 'coordinator';
  
  // Check if user can confirm/reject segments - admin/coordinator/tehnician
  const canConfirmSegments = user?.role === 'admin' || user?.role === 'coordinator' || user?.role === 'tehnician';
  
  // Load segments
  const loadSegments = async () => {
    if (!expanded) return; // Nu încărcăm până când nu expandăm
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.get(`/segments/?study_id=${studyId}`);
      setSegments(response.data);
    } catch (err: any) {
      console.error('Error loading segments:', err);
      setError(err.response?.data?.detail || 'Failed to load segments');
    } finally {
      setLoading(false);
    }
  };
  
  // Load segments when accordion is expanded or refreshKey changes
  useEffect(() => {
    if (expanded) {
      loadSegments();
    }
  }, [expanded, studyId, refreshKey]);
  
  // Open OHIF viewer for segment (filtered by series UIDs)
  const handleViewSegment = (segment: Segment) => {
    if (!pacsUrl || !studyUid) {
      alert('PACS URL or Study UID not available');
      return;
    }
    
    // Build OHIF Webviewer standalone URL with series filter
    // Deschide OHIF Viewer în popup maximized cu PACS custom și series UIDs
    void openOHIFViewer(studyUid, segment.series_uids, { pacsUrl });
  };
  
  // Open assign dialog
  const handleAssignSegment = (segment: Segment) => {
    setSelectedSegment(segment);
    setAssignDialogOpen(true);
  };

  // Open edit dialog
  const handleEditSegment = (segment: Segment) => {
    setEditingSegmentId(segment.id);
    setEditDialogOpen(true);
  };
  
  // Unassign doctor from segment
  const handleUnassignSegment = async (segment: Segment) => {
    if (!confirm(`Sigur doriți să dezalocați ${segment.assigned_doctor_name} de la acest segment?`)) {
      return;
    }
    
    try {
      await apiClient.delete(`/segments/${segment.id}/assign`);
      alert('Medic dezalocat cu succes');
      loadSegments();
      if (onSegmentUpdate) onSegmentUpdate();
    } catch (err: any) {
      console.error('Error unassigning doctor:', err);
      alert(err.response?.data?.detail || 'Eroare la dezalocare medic');
    }
  };
  
  // Confirm segment (pending → confirmed)
  const handleConfirmSegment = async (segment: Segment) => {
    if (!confirm(`Confirmați segment: ${formatBodyPartCode(segment.body_part_code)}?`)) {
      return;
    }
    
    try {
      await apiClient.patch(`/segments/${segment.id}`, {
        status: 'confirmed'
      });
      alert('Segment confirmat cu succes');
      loadSegments();
      if (onSegmentUpdate) onSegmentUpdate();
    } catch (err: any) {
      console.error('Error confirming segment:', err);
      alert(err.response?.data?.detail || 'Eroare la confirmarea segmentului');
    }
  };
  
  // Reject segment (pending → rejected)
  const handleRejectSegment = async (segment: Segment) => {
    if (!confirm(`Respingeți segment: ${formatBodyPartCode(segment.body_part_code)}? Acesta va fi marcat ca detecție incorectă.`)) {
      return;
    }
    
    try {
      await apiClient.patch(`/segments/${segment.id}`, {
        status: 'rejected'
      });
      alert('Segment respins cu succes');
      loadSegments();
      if (onSegmentUpdate) onSegmentUpdate();
    } catch (err: any) {
      console.error('Error rejecting segment:', err);
      alert(err.response?.data?.detail || 'Eroare la respingerea segmentului');
    }
  };
  
  // Handle assign dialog close
  const handleAssignDialogClose = (success: boolean) => {
    setAssignDialogOpen(false);
    setSelectedSegment(null);
    
    if (success) {
      loadSegments();
      if (onSegmentUpdate) onSegmentUpdate();
    }
  };
  
  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };
  
  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <ConfirmedIcon />;
      case 'pending':
        return <PendingIcon />;
      case 'rejected':
        return <RejectedIcon />;
      default:
        return null;
    }
  };
  
  // Format body part code for display
  const formatBodyPartCode = (code: string): string => {
    // CT_ABDOMEN_NATIVE → CT Abdomen (Native)
    const parts = code.split('_');
    if (parts.length >= 2) {
      const modality = parts[0];
      const bodyPart = parts.slice(1, -1).join(' ');
      const contrast = parts[parts.length - 1];
      
      return `${modality} ${bodyPart} (${contrast})`;
    }
    return code;
  };
  
  const handleOpenReport = (segment: Segment) => {
    // Navigate to report editor in per-segment mode
    navigate(`/reports/editor?segmentId=${segment.id}&studyId=${studyId}`);
  };

  return (
    <>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ p: 1, bgcolor: 'background.default', borderTop: '1px solid', borderColor: 'divider' }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          
          {error && (
            <Alert severity="error" sx={{ m: 1 }}>
              {error}
            </Alert>
          )}
          
          {!loading && !error && segments.length === 0 && (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ p: 2 }}>
              Nu s-au detectat segmente pentru acest studiu
            </Typography>
          )}
          
          {!loading && !error && segments.length > 0 && (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Segment</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Serii</TableCell>
                    <TableCell>Confidență</TableCell>
                    <TableCell>Alocat către</TableCell>
                    <TableCell align="right">Acțiuni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {segments.map((segment) => (
                    <TableRow key={segment.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {segment.display_name || formatBodyPartCode(segment.body_part_code)}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(segment.status) || undefined}
                          label={segment.status}
                          size="small"
                          color={getStatusColor(segment.status)}
                          variant="outlined"
                        />
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {segment.series_count} serii
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {(segment.confidence * 100).toFixed(0)}%
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        {segment.assigned_doctor_name ? (
                          <Typography variant="body2">
                            {segment.assigned_doctor_name}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" fontStyle="italic">
                            Nealocat
                          </Typography>
                        )}
                      </TableCell>
                      
                      {hideActions ? (
                        // Tehnician: doar confirmare segment dacă e pending
                        <TableCell align="right">
                          {segment.status === 'pending' && (
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                              <Tooltip title="Confirmă Segment">
                                <IconButton
                                  size="small"
                                  onClick={() => handleConfirmSegment(segment)}
                                  color="success"
                                >
                                  <ConfirmIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Respinge Segment">
                                <IconButton
                                  size="small"
                                  onClick={() => handleRejectSegment(segment)}
                                  color="error"
                                >
                                  <RejectIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </TableCell>
                      ) : (
                        // Alte roluri: acțiuni complete
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            {/* Edit button */}
                            {canManageSegments && (
                              <Tooltip title="Editează Segment">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditSegment(segment)}
                                  color="primary"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}

                            {/* View button */}
                            <Tooltip title="Deschide ZenRIS WebViewer">
                              <IconButton
                                size="small"
                                onClick={() => handleViewSegment(segment)}
                                disabled={!pacsUrl || !studyUid}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            {/* Report button - per-segment editor */}
                            <Tooltip title="Deschide Raport">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenReport(segment)}
                                color="primary"
                              >
                                <ConfirmedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            
                            {/* Confirm/Reject buttons (admin/coordinator/tehnician, pending segments only) */}
                            {canConfirmSegments && segment.status === 'pending' && (
                              <>
                                <Tooltip title="Confirmă Segment">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleConfirmSegment(segment)}
                                    color="success"
                                  >
                                    <ConfirmIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                
                                <Tooltip title="Respinge Segment">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRejectSegment(segment)}
                                    color="error"
                                  >
                                    <RejectIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            
                            {/* Assign/Unassign buttons (admin/coordinator only) */}
                            {canManageSegments && (
                              <>
                                {!segment.assigned_to_doctor_id ? (
                                  <Tooltip title="Alocă Medic">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleAssignSegment(segment)}
                                      color="success"
                                    >
                                      <AssignIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                ) : (
                                  <Tooltip title="Dezalocă Medic">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleUnassignSegment(segment)}
                                      color="error"
                                    >
                                      <UnassignIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </>
                            )}
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          {/* Radiation Dose Panel - afișat sub segmente */}
          <RadiationDosePanel studyId={studyId} />
        </Box>
      </Collapse>
      
      {/* Assign Dialog */}
      {selectedSegment && (
        <SegmentAssignDialog
          open={assignDialogOpen}
          onClose={handleAssignDialogClose}
          segment={selectedSegment}
        />
      )}

      {/* Edit Dialog */}
      {editingSegmentId && (
        <SegmentEditDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false)
            setEditingSegmentId(null)
          }}
          segmentId={editingSegmentId}
          onUpdate={() => {
            loadSegments()
            if (onSegmentUpdate) onSegmentUpdate()
          }}
        />
      )}
    </>
  );
};

export default SegmentAccordion;

